import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import StyledContainer from './components/styledContainer';
import Navigation from '../navigation';

const TERMS_OF_SERVICE_URL = '/terms-of-service.html';
const PRIVACY_POLICY_URL = '/privacy-policy.html';

/** Legal HTML supports en (default) and ko only */
function getLegalDocumentLang(i18nLanguage) {
    let code = i18nLanguage;
    if (!code && typeof localStorage !== 'undefined') {
        code = localStorage.getItem('language');
    }
    if (!code) return 'en';
    const normalized = String(code).toLowerCase();
    if (normalized === 'ko' || normalized.startsWith('ko-')) return 'ko';
    return 'en';
}

export const Terms = () => {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const termType = location.state?.type || 'terms_of_service';
    const legalLang = getLegalDocumentLang(i18n.language);
    const baseUrl =
        termType === 'privacy_policy'
            ? PRIVACY_POLICY_URL
            : TERMS_OF_SERVICE_URL;
    const documentUrl = `${baseUrl}?lang=${legalLang}`;
    const handleBack = () => {
        const source = location.state?.source;
        // signup에서 온 경우 step과 formData를 포함해서 돌아가기
        if (source === '/auth/signUp') {
            navigate('/auth/signUp', {
                state: {
                    step: location.state?.step || 1,
                    formData: location.state?.formData
                },
                replace: true
            });
        } else {
            navigate(-1);
        }
    };

    const handleIframeLoad = (event) => {
        const iframe = event.currentTarget;
        if (!iframe) return;
        try {
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!doc || !doc.body) return;
            const pageBg = theme.palette.figma?.mainBg || theme.palette.background.default;
            const textColor = theme.palette.text?.primary || '#111';
            const linkColor = '#1976d2';

            // Google Docs export HTML의 고정 폭(451pt)·큰 여백을 제거해 모바일 폭에 맞춘다.
            doc.body.className = '';
            doc.body.style.maxWidth = 'none';
            doc.body.style.width = '100%';
            doc.body.style.margin = '0';
            doc.body.style.padding = '20px';
            doc.body.style.boxSizing = 'border-box';
            doc.body.style.backgroundColor = pageBg;
            doc.body.style.color = textColor;

            const styleEl = doc.createElement('style');
            styleEl.textContent = `
                html, body { max-width: none !important; width: 100% !important; margin: 0 !important; padding: 20px !important; box-sizing: border-box !important; background: ${pageBg} !important; color: ${textColor} !important; }
                .doc-content, .c10, .c13 { max-width: none !important; padding: 20px !important; margin: 0 !important; background: ${pageBg} !important; color: ${textColor} !important; }
                p, li, span, div, h1, h2, h3, h4, h5, h6 { color: ${textColor} !important; }
                a { color: ${linkColor} !important; }
                p, li, span, div { word-break: keep-all; overflow-wrap: break-word; }
                /* 세로 스크롤 인디케이터 숨김 */
                html, body { -ms-overflow-style: none !important; scrollbar-width: none !important; }
                html::-webkit-scrollbar, body::-webkit-scrollbar { display: none !important; width: 0 !important; height: 0 !important; }
            `;
            doc.head?.appendChild(styleEl);
        } catch (e) {
            // same-origin이 아닌 경우 스타일 오버라이드를 건너뛴다.
        }
    };

    return (
        <StyledContainer sx={{ pb: 0 }}>
            <Navigation title={t(termType)} onLeftIconClick={handleBack} />
            <Box
                sx={{
                    width: '100%',
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    // HTML 파일은 그대로 두고 iframe 표시 영역만 좌우로 확장
                    px: { xs: 0, sm: 1 },
                  // pt: 1,
                    pb: 0,
                }}
            >
                <Box
                    component="iframe"
                    key={`${termType}-${legalLang}-${theme.palette.mode}`}
                    src={documentUrl}
                    title={termType}
                    onLoad={handleIframeLoad}
                    sx={{
                        width: 'calc(100% + 64px)',
                        marginLeft: '-64px',
                        marginRight: '-64px',
                        marginTop: '-40px',
                        flex: 1,
                        minHeight: 0,
                        border: 'none',
                        borderRadius: '10px',
                        backgroundColor: theme.palette.figma?.mainBg || theme.palette.background.default,
                    }}
                />
            </Box>
        </StyledContainer>
    );
};
